import { useState, useEffect, useMemo } from "react";
import {
  pdf,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  PDFViewer,
  Font,
} from "@react-pdf/renderer";
import {
  doc,
  updateDoc,
  collection,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
} from "firebase/firestore";
import { db, storage } from "../firebaseConfig";
import { ref, deleteObject, getStorage } from "firebase/storage";
import EditarReporte from "./EditarReporte";
import OpenRegular from "../fonts/OpenSans-Regular.ttf"; // Ruta de la fuente
import OpenBold from "../fonts/OpenSans-Bold.ttf"; // Ruta de la fuente
import Swal from "sweetalert2";
import Modal from "react-modal";
import { useAuth } from "../context/AuthContext";
import { v4 as uuidv4 } from "uuid";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia";

Font.register({
  family: "Open",
  fonts: [
    { src: OpenRegular, fontWeight: "normal" },
    { src: OpenBold, fontWeight: "bold" },
  ],
});

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    padding: 30,
    fontSize: 10,
    fontFamily: "Open",
  },
  pdfContainer: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  pdfViewer: {
    width: "80%",
    height: "90%",
    border: "1px solid #ccc",
    borderRadius: 5,
    overflow: "hidden",
  },
  closeButton: {
    color: "#fff",
    top: "15px",
    fontSize: "20px",
  },
  headerContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  logoContainer: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
  logo: {
    width: 120,
    marginBottom: 5,
  },
  orderTitleContainer: {
    alignItems: "flex-end",
  },
  orderTitle: {
    fontSize: 14,
    color: "#3579CB",
    fontWeight: "bold",
    letterSpacing: "1px",
  },
  orderNumber: {
    fontSize: 11,
    backgroundColor: "#FF0000",
    color: "#fff",
    fontWeight: "bold",
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginTop: 2,
    letterSpacing: "0.5px",
  },
  infoSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  infoLeft: {
    width: "45%",
  },
  infoRight: {
    width: "45%",
    alignItems: "flex-end",
  },
  infoTitle: {
    fontWeight: "bold",
    fontSize: 10,
    marginBottom: 10,
    textDecoration: "underline",
  },
  infoText: {
    fontSize: 9,
    marginBottom: 2,
  },
  infoTextRazon: {
    fontSize: 9,
    marginBottom: 2,
    fontWeight: "bold",
  },
  dataSection: {
    flexDirection: "row",
    marginBottom: 10,
    gap: 2,
  },
  dataBox: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#eaeaea",
    backgroundColor: "#eee",
    padding: 8,
    justifyContent: "center",
  },
  dataBoxTitle: {
    fontSize: 8,
    color: "#555",
    fontWeight: "semibold",
    textAlign: "center",
    letterSpacing: "0.1px",
  },
  dataBoxValue: {
    fontSize: 8,
    fontWeight: 600,
    marginTop: 2,
    textTransform: "uppercase",
    textAlign: "center",
    letterSpacing: "0.1px",
    minHeight: 10, // Establece una altura mínima para evitar colapso
  },
  tableContainer: {
    marginTop: 10,
  },
  tableHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#555",
  },
  tableHeaderCell: {
    paddingVertical: 6,
    paddingHorizontal: 4,
    fontSize: 9,
    backgroundColor: "#555",
    color: "#fff",
    textAlign: "center",
  },
  tableRow: {
    flexDirection: "row",
  },
  tableCell: {
    fontSize: 9,
    padding: 4,
    textAlign: "right",
  },
  tableCellImage: {
    borderWidth: 1,
    borderColor: "#eaeaea",
    fontSize: 9,
    padding: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  noBorderCell: {
    borderWidth: 0,
    borderColor: "transparent",
    borderStyle: "none",
  },
  signContainer: {
    flexDirection: "row",
    marginTop: 50,
    borderWidth: 1,
    borderColor: "#ddd",
    backgroundColor: "#F5F7F9", // o #FFF, según prefieras
    pageBreakInside: "avoid",
  },
  conditionsColumn: {
    width: "60%",
    padding: 10,
  },
  signatureColumn: {
    width: "40%",
    padding: 10,
    borderLeftWidth: 2,
    borderLeftColor: "#3579CB",
  },
  conditionsTitle: {
    fontWeight: "bold",
    fontSize: 10,
    color: "#3579CB", // Como en tu ejemplo
    marginBottom: 6,
  },
  conditionItem: {
    fontSize: 9,
    marginBottom: 4,
  },
  signatureTitle: {
    fontWeight: "bold",
    fontSize: 10,
    color: "#3579CB",
    textAlign: "center",
    marginBottom: 10,
  },
  userName: {
    fontSize: 10,
    fontWeight: "bold",
    textAlign: "center",
  },
  userTitle: {
    fontSize: 9,
    textAlign: "center",
  },
  infoFirma: {
    display: "flex",
    margin: "0 auto",
    alignItems: "center",
    paddingTop: "30px",
  },
  noBorderRow: {
    borderWidth: 0,
    borderColor: "transparent",
    borderStyle: "none",
  },
});

const ResumenReporte = ({
  reporte,
  isOpen,
  onClose,
  clientes,
  setReportes,
  reportes,
  onClonar,
}) => {
  const { user, teamOwnerId } = useAuth();
  const ownerUid = teamOwnerId || user.uid;
  const { formatDate } = useFunctionsPrevia();
  const [showOptions, setShowOptions] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showSummary, setShowSummary] = useState(true);
  const [pdfVisible, setPdfVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [showAdjuntosModal, setShowAdjuntosModal] = useState(false);
  const [adjuntos, setAdjuntos] = useState([]);

  useEffect(() => {
    if (showAdjuntosModal && reporte.archivos) {
      setAdjuntos(reporte.archivos);
    }
  }, [reporte, showAdjuntosModal]);

  const handleDeleteArchivo = async (archivo) => {
    // Mostrar mensaje de confirmación
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: `Estás a punto de eliminar el archivo "${archivo.nombre}". Esta acción no se puede deshacer.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    // Si el usuario confirma, proceder con la eliminación
    if (result.isConfirmed) {
      try {
        // 1. Eliminar el archivo de Firebase Storage
        const fileRef = ref(storage, archivo.url); // Suponiendo que 'url' es el path en Storage
        await deleteObject(fileRef);

        // 2. Actualizar la cotización en Firestore para eliminar el archivo
        const reporteRef = doc(db, "obras", reporte.id);
        await updateDoc(reporteRef, {
          archivos: reporte.archivos.filter((a) => a.url !== archivo.url),
        });

        // 3. Actualizar el estado local de adjuntos
        setAdjuntos((prevAdjuntos) =>
          prevAdjuntos.filter((a) => a.url !== archivo.url)
        );

        // 4. Actualizar la lista de órdenes si es necesario
        if (typeof setReportes === "function") {
          setReportes((prevReportes) =>
            prevReportes.map((c) =>
              c.id === reporte.id
                ? {
                    ...c,
                    archivos: c.archivos.filter((a) => a.url !== archivo.url),
                  }
                : c
            )
          );
        }

        // Mostrar una notificación de éxito
        Swal.fire({
          icon: "success",
          title: "Archivo eliminado",
          text: `El archivo "${archivo.nombre}" ha sido eliminado correctamente.`,
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Error al eliminar el archivo:", error);
        Swal.fire({
          icon: "error",
          title: "Error al eliminar",
          text: "Hubo un problema al eliminar el archivo. Por favor, intenta nuevamente.",
        });
      }
    }
  };

  const handleEliminar = async () => {
    const result = await Swal.fire({
      title: `¿Seguro que quieres eliminar la cotización "${reporte.numeroObra}"?`,
      text: "Esta acción no se puede deshacer",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (!result.isConfirmed) return;

    // Cierra el modal
    onClose?.();

    try {
      const storage = getStorage();

      // 1) Borrar todos los archivos en Storage y Firestore
      const archivosCol = collection(
        db,
        "usuarios",
        ownerUid,
        "obras",
        reporte.id,
        "archivos"
      );
      const archivosSnap = await getDocs(archivosCol);
      for (const archivoDoc of archivosSnap.docs) {
        const { path } = archivoDoc.data();
        if (path) {
          await deleteObject(ref(storage, path));
        }
        await deleteDoc(archivoDoc.ref);
      }

      // 2) Eliminar la cotización
      const cotRef = doc(db, "usuarios", ownerUid, "obras", reporte.id);
      await deleteDoc(cotRef);

      // 3) Actualizar estado local
      if (typeof setReportes === "function") {
        setReportes((prev) => prev.filter((r) => r.id !== reporte.id));
      }

      // 4) Notificación de éxito
      await Swal.fire({
        icon: "success",
        title: "Eliminada",
        text: `El reporte "${reporte.numeroObra}" se eliminó correctamente.`,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error al eliminar el reporte:", error);
      Swal.fire({
        icon: "error",
        title: "Error al eliminar",
        text: "No se pudo eliminar el reporte. Intenta de nuevo.",
      });
    }
  };

  const handleClonar = async () => {
    try {
      // 1) Leer configuración (sin avanzar el contador)
      const configRef = doc(
        db,
        "usuarios",
        ownerUid,
        "configuracion",
        "numeroObra"
      );
      const configSnap = await getDoc(configRef);
      if (!configSnap.exists()) {
        console.error("No se encontró configuración de número de obra");
        return;
      }
      const { prefijo = "RO-", siguienteNumero = 1 } = configSnap.data();

      // 2) Formatear número
      const numeroFormateado = String(siguienteNumero).padStart(4, "0");
      const posibleNumero = `${prefijo}${numeroFormateado}`;

      // 3) Generar nuevo ID
      const nuevoId = uuidv4();

      // 4) Construir el objeto clon *incluyendo* el array de archivos
      const clon = {
        ...reporte,
        id: nuevoId,
        fechaReporte: new Date().toISOString(),
        numeroObra: posibleNumero,
        estado: "Pendiente",
        archivos: reporte.archivos || [],
      };

      // 5) Copiar subcolección de archivos al nuevo reporte
      const colecAnt = collection(
        db,
        "usuarios",
        ownerUid,
        "obras",
        reporte.id,
        "archivos"
      );
      const colecNuevo = collection(
        db,
        "usuarios",
        ownerUid,
        "obras",
        nuevoId,
        "archivos"
      );
      const snapAnt = await getDocs(colecAnt);
      for (const docArchivo of snapAnt.docs) {
        // Mantenemos el mismo ID de cada archivo
        await setDoc(doc(colecNuevo, docArchivo.id), docArchivo.data());
      }

      // 6) Abrir modal de edición en modo clonación
      onClonar?.(clon, /* cloneMode = */ true);
      onClose?.();
    } catch (error) {
      console.error("Error al clonar el reporte:", error);
      Swal.fire({
        icon: "error",
        title: "Error al clonar",
        text: "Hubo un problema al clonar el reporte. Intenta nuevamente.",
      });
    }
  };

  const obtenerIconoArchivo = (nombreArchivo) => {
    const extension = nombreArchivo.split(".").pop().toLowerCase();
    switch (extension) {
      case "xls":
      case "xlsx":
      case "csv":
        return { icon: "bi-file-earmark-excel", color: "green" };
      case "doc":
      case "docx":
        return { icon: "bi-file-earmark-word", color: "blue" };
      case "pdf":
        return { icon: "bi-file-earmark-pdf", color: "red" };
      case "jpg":
      case "jpeg":
      case "png":
      case "svg":
        return { icon: "bi-image", color: "blue" };
      default:
        return { icon: "bi-file-earmark", color: "gray" }; // Ícono genérico
    }
  };

  useEffect(() => {
    if (!isOpen && onClose) {
      onClose();
    }
  }, [isOpen, onClose]);

  const handlePrint = () => {
    setPdfVisible(true);
  };

  const handleClosePDF = () => {
    setPdfVisible(false);
  };

  const memoizedPdfDocument = useMemo(() => {
    if (!reporte || !reporte.productosSeleccionados.length) {
      return null;
    }

    const firstThree = reporte.productosSeleccionados.slice(0, 3);
    const rest = reporte.productosSeleccionados.slice(3);

    const promedioAvance =
      reporte.productosSeleccionados.reduce(
        (acum, prod) => acum + (prod.avance || 0),
        0
      ) / reporte.productosSeleccionados.length;

    const promedioFormateado = promedioAvance.toFixed(2); // "28.67"

    // Buscar el proveedor
    const clienteSeleccionado = Array.isArray(clientes)
      ? clientes.find((p) => p.id === reporte.cliente) || {}
      : {};

    const {
      empresa = "",
      rfc = "",
      domicilio = "",
      numeroExt = "",
      numeroInt = "",
      colonia = "",
      ciudad = "",
      estado = "",
      codigoPostal = "",
    } = clienteSeleccionado;

    // Particionamos los productos por páginas
    const productosPorPagina = 3;
    const paginasDeProductos = [];

    for (
      let i = 0;
      i < reporte.productosSeleccionados.length;
      i += productosPorPagina
    ) {
      paginasDeProductos.push(
        reporte.productosSeleccionados.slice(i, i + productosPorPagina)
      );
    }

    // Cálculos de totales
    const subtotal = reporte.productosSeleccionados.reduce(
      (acumulador, producto) => {
        const precioBruto = (producto.tarifa || 0) * (producto.cantidad || 0);
        let descuentoProducto = 0;
        if (producto.tipoDescuento === "%") {
          descuentoProducto = precioBruto * ((producto.descuento || 0) / 100);
        } else if (producto.tipoDescuento === "$") {
          descuentoProducto = producto.descuento || 0;
        }
        return acumulador + (precioBruto - descuentoProducto);
      },
      0
    );

    const descuentoPorcentaje = reporte.descuento || 0;
    const descuentoAplicado = subtotal * (descuentoPorcentaje / 100);
    const subtotalConDescuento = subtotal - descuentoAplicado;
    const ivaRate = 0.16;
    const ivaTotal = subtotalConDescuento * ivaRate;
    const total = subtotalConDescuento + ivaTotal;

    return (
      <Document>
        <Page size="A4" style={styles.page} orientation="landscape" wrap>
          {/* ENCABEZADO */}
          <View style={styles.headerContainer}>
            <View style={styles.logoContainer}>
              <Image src="/img/logo-iciamex2.png" style={styles.logo} />
            </View>
            <View style={styles.orderTitleContainer}>
              <Text style={styles.orderTitle}>GENERADOR DE OBRA</Text>
              <Text style={styles.orderNumber}>
                #{reporte.numeroObra?.toString().padStart(4, "0")}
              </Text>
            </View>
          </View>

          <View style={{ marginBottom: 10 }}>
            <View
              style={{
                borderTopWidth: 2,
                borderTopColor: "#3579CB",
                borderColor: "#000",
                position: "relative",
                borderWidth: 0.4,
                backgroundColor: "#3579CB",
              }}
            />
            <View
              style={{
                position: "absolute",
                top: -3,
                left: 0,
                right: 0,
                height: 3,
                backgroundColor: "#3579CB",
                width: "18%",
              }}
            />
          </View>

          {/* Proveedor / Facturación */}
          <View style={styles.infoSection}>
            <View style={styles.infoLeft}>
              <Text style={styles.infoTitle}>CLIENTE:</Text>
              <Text style={styles.infoTextRazon}>{empresa}</Text>
              <Text style={styles.infoText}>
                {domicilio} {numeroExt}
                {numeroInt ? ` Int. ${numeroInt}` : ""} {colonia} C.P.{" "}
                {codigoPostal}
              </Text>
              <Text style={styles.infoText}>
                {ciudad}, {estado}, México.
              </Text>
              <Text style={styles.infoText}>RFC: {rfc}</Text>
            </View>

            <View style={styles.infoRight}>
              <Text style={styles.infoTitle}>PROVEEDOR:</Text>
              <Text style={styles.infoTextRazon}>
                UNIFICACION PARA LA AUTOMATIZACION
              </Text>
              <Text style={styles.infoText}>
                LIC. RAMIRO HERNÁNDEZ 448, COL. LAS PINTITAS,
              </Text>
              <Text style={styles.infoText}>EL SALTO, JAL. C.P. 45693</Text>
              <Text style={styles.infoText}>RFC: UAU151222IT1</Text>
            </View>
          </View>

          {/* Datos Principales */}
          <View style={styles.dataSection}>
            <View style={styles.dataBox}>
              <Text style={styles.dataBoxTitle}>FECHA:</Text>
              <Text style={styles.dataBoxValue}>
                {formatDate(reporte.fechaReporte)}
              </Text>
            </View>
            <View style={styles.dataBox}>
              <Text style={styles.dataBoxTitle}>ORDEN DE COMPRA:</Text>
              <Text style={styles.dataBoxValue}>
                {reporte.ordenDeCompra || "N/A"}
              </Text>
            </View>
            <View style={styles.dataBox}>
              <Text style={styles.dataBoxTitle}>USUARIO:</Text>
              <Text style={styles.dataBoxValue}>
                {reporte.usuario || "EXW"}
              </Text>
            </View>
            <View style={styles.dataBox}>
              <Text style={styles.dataBoxTitle}>PLANTA:</Text>
              <Text style={styles.dataBoxValue}>{reporte.planta}</Text>
            </View>
            <View style={styles.dataBox}>
              <Text style={styles.dataBoxTitle}>ÁREA:</Text>
              <Text style={styles.dataBoxValue}>{reporte.area}</Text>
            </View>
          </View>

          {/* TABLA - PRIMEROS 3 PRODUCTOS (o todos si <= 3) */}
          <View style={styles.tableContainer}>
            {/* Encabezado de tabla */}
            <View style={styles.tableHeaderRow}>
              <Text style={[styles.tableHeaderCell, { flex: 0.5 }]}>PDA</Text>
              <Text style={[styles.tableHeaderCell, { flex: 3 }]}>
                DESCRIPCIÓN
              </Text>
              <Text style={[styles.tableHeaderCell, { flex: 1 }]}>UNIDAD</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1 }]}>
                CANTIDAD
              </Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.2 }]}>
                PRECIO
              </Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.2 }]}>
                IMPORTE
              </Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>
                CANT. INSTALADA
              </Text>
              <Text style={[styles.tableHeaderCell, { flex: 1 }]}>
                AVANCE %
              </Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>
                IMAGEN
              </Text>
            </View>

            {/* Filas de firstThree */}
            {firstThree.map((producto, indexProd) => (
              <View
                key={indexProd}
                style={[
                  styles.tableRow,
                  {
                    backgroundColor: indexProd % 2 === 0 ? "#FFF" : "#EFF2F2",
                    pageBreakInside: "avoid",
                  },
                ]}
                wrap={false}
              >
                {/* Celda 1: PDA */}
                <Text
                  style={[
                    styles.tableCell,
                    {
                      flex: 0.5,
                      textAlign: "center",
                      pageBreakInside: "avoid",
                    },
                  ]}
                  wrap={false}
                >
                  {indexProd + 1}
                </Text>

                {/* Celda 2: DESCRIPCIÓN */}
                <Text
                  style={[
                    styles.tableCell,
                    { flex: 3, textAlign: "left", pageBreakInside: "avoid" },
                  ]}
                  wrap={false}
                >
                  {producto.nombre || "Sin descripción"}
                </Text>

                {/* Celda 3: UNIDAD */}
                <Text
                  style={[
                    styles.tableCell,
                    {
                      flex: 1,
                      textAlign: "center",
                      textTransform: "uppercase",
                      pageBreakInside: "avoid",
                    },
                  ]}
                  wrap={false}
                >
                  {producto.unidad}
                </Text>

                {/* Celda 4: CANTIDAD */}
                <Text
                  style={[
                    styles.tableCell,
                    { flex: 1, textAlign: "center", pageBreakInside: "avoid" },
                  ]}
                  wrap={false}
                >
                  {producto.cantidad}
                </Text>

                {/* Celda 5: PRECIO */}
                <Text
                  style={[
                    styles.tableCell,
                    { flex: 1.2, textAlign: "right", pageBreakInside: "avoid" },
                  ]}
                  wrap={false}
                >
                  $
                  {parseFloat(producto.tarifa || 0)
                    .toFixed(2)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                </Text>

                {/* Celda 6: IMPORTE */}
                <Text
                  style={[
                    styles.tableCell,
                    {
                      flex: 1.2,
                      textAlign: "center",
                      pageBreakInside: "avoid",
                    },
                  ]}
                  wrap={false}
                >
                  $
                  {parseFloat(producto.importe || 0)
                    .toFixed(2)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                </Text>

                {/* Celda 7: CANT. INSTALADA */}
                <Text
                  style={[
                    styles.tableCell,
                    {
                      flex: 1.5,
                      textAlign: "center",
                      pageBreakInside: "avoid",
                    },
                  ]}
                  wrap={false}
                >
                  {producto.cantidadInstalada || 0}
                </Text>

                {/* Celda 8: AVANCE % */}
                <Text
                  style={[
                    styles.tableCell,
                    { flex: 1, textAlign: "center", pageBreakInside: "avoid" },
                  ]}
                  wrap={false}
                >
                  {producto.avance || 0}%
                </Text>

                {/* Celda 9: IMAGEN */}
                <View
                  style={[
                    styles.tableCellImage,
                    styles.noBorderCell,
                    { flex: 1.5, pageBreakInside: "avoid" },
                  ]}
                  wrap={false}
                >
                  {producto.imagenUrl && (
                    <Image
                      style={{ width: 60, height: 50, objectFit: "contain" }}
                      src={producto.imagenUrl}
                    />
                  )}
                </View>
              </View>
            ))}
          </View>

          {/* Footer fijo de la primera página */}
          <View
            fixed
            style={{
              position: "absolute",
              bottom: 0,
              left: 30,
              right: 30,
              flexDirection: "column",
              alignItems: "stretch",
              backgroundColor: "#FFFFFF",
            }}
          >
            <View
              style={{
                borderTopWidth: 0.5,
                borderTopColor: "#999999",
                marginTop: 5,
                paddingTop: 5,
              }}
            >
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 10,
                }}
              >
                <Text style={{ fontSize: 8, color: "#999999" }}>
                  UNIFICACION PARA LA AUTOMATIZACION SA DE CV
                </Text>
                <Text style={{ fontSize: 8, color: "#999" }}>
                  TEL: 33 2192 8621 / 33 3619 2439
                </Text>
              </View>
            </View>
          </View>

          {/* 
      Si NO hay rest => esta es la última página.
      => MOSTRAMOS Totales, Firma, y el párrafo "Para cualquier duda..." 
      anclado al final.
    */}
          {rest.length === 0 && (
            <View>
              {/* Totales (SUBTOTAL, IVA, TOTAL) */}
              <View style={[styles.tableRow, styles.noBorderRow]}>
                <Text
                  style={[
                    styles.tableCell,
                    styles.noBorderCell,
                    { flex: 5.95 },
                  ]}
                />
                <Text
                  style={[
                    styles.tableCell,
                    styles.noBorderCell,
                    {
                      flex: 1.2,
                      backgroundColor: "#555",
                      textAlign: "center",
                      color: "#fff",
                      fontWeight: "bold",
                    },
                  ]}
                >
                  SUBTOTAL
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    {
                      flex: 1.2,
                      textAlign: "right",
                      fontWeight: "bold",
                      fontSize: "8px",
                    },
                  ]}
                >
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(subtotal)}
                </Text>

                <Text
                  style={[
                    styles.tableCell,
                    styles.noBorderCell,
                    {
                      flex: 1.5,
                      backgroundColor: "#3579CB",
                      color: "#FFF",
                      fontWeight: "bold",
                      textAlign: "center",
                    },
                  ]}
                >
                  AVANCE TOTAL %
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    {
                      flex: 1,
                      fontWeight: "bold",
                      textAlign: "center",
                    },
                  ]}
                >
                  {promedioFormateado}%
                </Text>
                <Text
                  style={[styles.tableCell, styles.noBorderCell, { flex: 1.5 }]}
                />
              </View>

              <View style={[styles.tableRow, styles.noBorderRow]}>
                <Text
                  style={[
                    styles.tableCell,
                    styles.noBorderCell,
                    { flex: 5.95 },
                  ]}
                />
                <Text
                  style={[
                    styles.tableCell,
                    styles.noBorderCell,
                    {
                      flex: 1.2,
                      textAlign: "center",
                      fontWeight: "bold",
                      backgroundColor: "#555",
                      color: "#fff",
                    },
                  ]}
                >
                  IVA
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    {
                      flex: 1.2,
                      textAlign: "right",
                      fontWeight: "bold",
                      fontSize: "8px",
                    },
                  ]}
                >
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(ivaTotal)}
                </Text>
                <Text
                  style={[styles.tableCell, styles.noBorderCell, { flex: 1.5 }]}
                />
                <Text
                  style={[styles.tableCell, styles.noBorderCell, { flex: 1 }]}
                />
                <Text
                  style={[styles.tableCell, styles.noBorderCell, { flex: 1.5 }]}
                />
              </View>

              <View style={[styles.tableRow, styles.noBorderRow]}>
                <Text
                  style={[
                    styles.tableCell,
                    styles.noBorderCell,
                    { flex: 5.95 },
                  ]}
                />
                <Text
                  style={[
                    styles.tableCell,
                    styles.noBorderCell,
                    {
                      flex: 1.2,
                      textAlign: "center",
                      fontWeight: "bold",
                      backgroundColor: "#555",
                      color: "#fff",
                    },
                  ]}
                >
                  TOTAL
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    {
                      flex: 1.2,
                      textAlign: "right",
                      fontSize: 8,
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                    },
                  ]}
                >
                  {`${new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(total)} ${reporte.divisa}`}
                </Text>
                <Text
                  style={[styles.tableCell, styles.noBorderCell, { flex: 1.5 }]}
                />
                <Text
                  style={[styles.tableCell, styles.noBorderCell, { flex: 1 }]}
                />
                <Text
                  style={[styles.tableCell, styles.noBorderCell, { flex: 1.5 }]}
                />
              </View>

              {/* Firma y condiciones */}
              <View wrap={false} style={styles.signContainer}>
                <View style={styles.conditionsColumn}>
                  <Text style={styles.conditionsTitle}>
                    CONDICIONES / OBSERVACIONES:
                  </Text>
                  <Text style={styles.conditionItem}>{reporte.terminos}</Text>
                </View>
                <View style={styles.signatureColumn}>
                  <Text style={styles.signatureTitle}>
                    Firma de Conformidad Usuario
                  </Text>
                  <View style={styles.infoFirma}>
                    <Text style={styles.userName}>{reporte.usuario}</Text>
                    <Text style={styles.userTitle}>Proyectos</Text>
                  </View>
                </View>
              </View>
            </View>
          )}
          {rest.length === 0 && (
            <View
              wrap={false}
              style={{
                position: "absolute",
                bottom: 30, // Pegado abajo
                left: 30,
                right: 30,
              }}
            >
              <Text style={{ fontSize: 10 }}>
                Para cualquier duda, comunicarse al teléfono: 33 2192 8621 ó al
                correo contacto@iciamex.com
              </Text>
            </View>
          )}
        </Page>
        {/*
    SEGUNDA PÁGINA (y subsecuentes) SOLO SI rest.length > 0
    => Aquí se listan los productos restantes y se muestran
       totales, firma y texto final.
  */}
        {rest.length > 0 && (
          <Page size="A4" style={styles.page} orientation="landscape" wrap>
            {/* Cabecera de tabla con fixed */}
            <View style={styles.tableContainer}>
              <View style={styles.tableHeaderRow} fixed>
                <Text style={[styles.tableHeaderCell, { flex: 0.5 }]}>PDA</Text>
                <Text style={[styles.tableHeaderCell, { flex: 3 }]}>
                  DESCRIPCIÓN
                </Text>
                <Text style={[styles.tableHeaderCell, { flex: 1 }]}>
                  UNIDAD
                </Text>
                <Text style={[styles.tableHeaderCell, { flex: 1 }]}>
                  CANTIDAD
                </Text>
                <Text style={[styles.tableHeaderCell, { flex: 1.2 }]}>
                  PRECIO
                </Text>
                <Text style={[styles.tableHeaderCell, { flex: 1.2 }]}>
                  IMPORTE
                </Text>
                <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>
                  CANT. INSTALADA
                </Text>
                <Text style={[styles.tableHeaderCell, { flex: 1 }]}>
                  AVANCE %
                </Text>
                <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>
                  IMAGEN
                </Text>
              </View>

              {/* Filas de productos en rest */}
              {rest.map((producto, idx) => (
                <View
                  key={idx}
                  style={[
                    styles.tableRow,
                    {
                      backgroundColor: idx % 2 === 0 ? "#FFF" : "#EFF2F2",
                    },
                  ]}
                >
                  {/* 1) PDA */}
                  <Text
                    style={[
                      styles.tableCell,
                      {
                        flex: 0.5,
                        textAlign: "center",
                        pageBreakInside: "avoid",
                      },
                    ]}
                    wrap={false}
                  >
                    {idx + 1 + firstThree.length}
                  </Text>

                  {/* 2) DESCRIPCIÓN */}
                  <Text
                    style={[
                      styles.tableCell,
                      { flex: 3, textAlign: "left", pageBreakInside: "avoid" },
                    ]}
                    wrap={false}
                  >
                    {producto.nombre || "Sin descripción"}
                  </Text>

                  {/* 3) UNIDAD */}
                  <Text
                    style={[
                      styles.tableCell,
                      {
                        flex: 1,
                        textAlign: "center",
                        textTransform: "uppercase",
                        pageBreakInside: "avoid",
                      },
                    ]}
                    wrap={false}
                  >
                    {producto.unidad}
                  </Text>

                  {/* 4) CANTIDAD */}
                  <Text
                    style={[
                      styles.tableCell,
                      {
                        flex: 1,
                        textAlign: "center",
                        pageBreakInside: "avoid",
                      },
                    ]}
                    wrap={false}
                  >
                    {producto.cantidad}
                  </Text>

                  {/* 5) PRECIO */}
                  <Text
                    style={[
                      styles.tableCell,
                      {
                        flex: 1.2,
                        textAlign: "right",
                        pageBreakInside: "avoid",
                      },
                    ]}
                    wrap={false}
                  >
                    $
                    {parseFloat(producto.tarifa || 0)
                      .toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  </Text>

                  {/* 6) IMPORTE */}
                  <Text
                    style={[
                      styles.tableCell,
                      {
                        flex: 1.2,
                        textAlign: "center",
                        pageBreakInside: "avoid",
                      },
                    ]}
                    wrap={false}
                  >
                    $
                    {parseFloat(producto.importe || 0)
                      .toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  </Text>

                  {/* 7) CANT. INSTALADA */}
                  <Text
                    style={[
                      styles.tableCell,
                      {
                        flex: 1.5,
                        textAlign: "center",
                        pageBreakInside: "avoid",
                      },
                    ]}
                    wrap={false}
                  >
                    {producto.cantidadInstalada || 0}
                  </Text>

                  {/* 8) AVANCE % */}
                  <Text
                    style={[
                      styles.tableCell,
                      {
                        flex: 1,
                        textAlign: "center",
                        pageBreakInside: "avoid",
                      },
                    ]}
                    wrap={false}
                  >
                    {producto.avance || 0}%
                  </Text>

                  {/* 9) IMAGEN */}
                  <View
                    style={[
                      styles.tableCellImage,
                      styles.noBorderCell,
                      { flex: 1.5, pageBreakInside: "avoid" },
                    ]}
                    wrap={false}
                  >
                    {producto.imagenUrl && (
                      <Image
                        style={{ width: 60, height: 50, objectFit: "contain" }}
                        src={producto.imagenUrl}
                      />
                    )}
                  </View>
                </View>
              ))}

              {/* Totales, Firma y Condiciones (sin wrap) */}
              <View wrap={false}>
                {/* SUBTOTAL */}
                <View style={[styles.tableRow, styles.noBorderRow]}>
                  <Text
                    style={[
                      styles.tableCell,
                      styles.noBorderCell,
                      { flex: 5.95 },
                    ]}
                  />
                  <Text
                    style={[
                      styles.tableCell,
                      styles.noBorderCell,
                      {
                        flex: 1.2,
                        backgroundColor: "#555",
                        textAlign: "center",
                        color: "#fff",
                        fontWeight: "bold",
                      },
                    ]}
                  >
                    SUBTOTAL
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      {
                        flex: 1.2,
                        textAlign: "right",
                        fontWeight: "bold",
                        fontSize: "8px",
                      },
                    ]}
                  >
                    {new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(subtotal)}
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      styles.noBorderCell,
                      {
                        flex: 1.5,
                        backgroundColor: "#3579CB",
                        color: "#FFF",
                        fontWeight: "bold",
                        textAlign: "center",
                      },
                    ]}
                  >
                    AVANCE TOTAL %
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      {
                        flex: 1,
                        fontWeight: "bold",
                        textAlign: "center",
                      },
                    ]}
                  >
                    {promedioFormateado}%
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      styles.noBorderCell,
                      { flex: 1.5 },
                    ]}
                  />
                </View>

                {/* IVA */}
                <View style={[styles.tableRow, styles.noBorderRow]}>
                  <Text
                    style={[
                      styles.tableCell,
                      styles.noBorderCell,
                      { flex: 5.95 },
                    ]}
                  />
                  <Text
                    style={[
                      styles.tableCell,
                      styles.noBorderCell,
                      {
                        flex: 1.2,
                        textAlign: "center",
                        fontWeight: "bold",
                        backgroundColor: "#555",
                        color: "#fff",
                      },
                    ]}
                  >
                    IVA
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      {
                        flex: 1.2,
                        textAlign: "right",
                        fontWeight: "bold",
                        fontSize: "8px",
                      },
                    ]}
                  >
                    {new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(ivaTotal)}
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      styles.noBorderCell,
                      { flex: 1.5 },
                    ]}
                  />
                  <Text
                    style={[styles.tableCell, styles.noBorderCell, { flex: 1 }]}
                  />
                  <Text
                    style={[
                      styles.tableCell,
                      styles.noBorderCell,
                      { flex: 1.5 },
                    ]}
                  />
                </View>

                {/* TOTAL */}
                <View style={[styles.tableRow, styles.noBorderRow]}>
                  <Text
                    style={[
                      styles.tableCell,
                      styles.noBorderCell,
                      { flex: 5.95 },
                    ]}
                  />
                  <Text
                    style={[
                      styles.tableCell,
                      styles.noBorderCell,
                      {
                        flex: 1.2,
                        textAlign: "center",
                        fontWeight: "bold",
                        backgroundColor: "#555",
                        color: "#fff",
                      },
                    ]}
                  >
                    TOTAL
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      {
                        flex: 1.2,
                        textAlign: "right",
                        fontSize: 8,
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                      },
                    ]}
                  >
                    {`${new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(total)} ${reporte.divisa}`}
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      styles.noBorderCell,
                      { flex: 1.5 },
                    ]}
                  />
                  <Text
                    style={[styles.tableCell, styles.noBorderCell, { flex: 1 }]}
                  />
                  <Text
                    style={[
                      styles.tableCell,
                      styles.noBorderCell,
                      { flex: 1.5 },
                    ]}
                  />
                </View>

                {/* Firma y condiciones */}
                <View wrap={false} style={styles.signContainer}>
                  <View style={styles.conditionsColumn}>
                    <Text style={styles.conditionsTitle}>
                      CONDICIONES / OBSERVACIONES:
                    </Text>
                    <Text style={styles.conditionItem}>{reporte.terminos}</Text>
                  </View>
                  <View style={styles.signatureColumn}>
                    <Text style={styles.signatureTitle}>
                      Firma de Conformidad Usuario
                    </Text>
                    <View style={styles.infoFirma}>
                      <Text style={styles.userName}>{reporte.usuario}</Text>
                      <Text style={styles.userTitle}>Proyectos</Text>
                    </View>
                  </View>
                </View>
              </View>
            </View>

            {/* Footer fijo en la segunda página */}
            <View
              fixed
              style={{
                position: "absolute",
                bottom: 0,
                left: 30,
                right: 30,
                flexDirection: "column",
                alignItems: "stretch",
                backgroundColor: "#FFFFFF",
              }}
            >
              <View
                style={{
                  borderTopWidth: 0.5,
                  borderTopColor: "#999999",
                  marginTop: 5,
                  paddingTop: 5,
                }}
              >
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 10,
                  }}
                >
                  <Text style={{ fontSize: 8, color: "#999999" }}>
                    UNIFICACION PARA LA AUTOMATIZACION SA DE CV
                  </Text>
                  <Text style={{ fontSize: 8, color: "#999" }}>
                    TEL: 33 2192 8621 / 33 3619 2439
                  </Text>
                </View>
              </View>
            </View>

            {/* Texto final anclado al borde inferior (solo en esta última página) */}
            <View
              wrap={false}
              style={{
                position: "absolute",
                bottom: 30,
                left: 30,
                right: 30,
              }}
            >
              <Text style={{ fontSize: 10 }}>
                Para cualquier duda, comunicarse al teléfono: 33 2192 8621 ó al
                correo contacto@iciamex.com
              </Text>
            </View>
          </Page>
        )}
      </Document>
    );
  }, [reporte, clientes, formatDate]);

  useEffect(() => {
    let isMounted = true;
    let url;

    const generateBlobUrl = async () => {
      if (!memoizedPdfDocument) {
        setPdfBlobUrl(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      const blob = await pdf(memoizedPdfDocument).toBlob();
      if (isMounted) {
        url = URL.createObjectURL(blob);
        setPdfBlobUrl(url);
        setLoading(false);
      }
    };

    generateBlobUrl();

    return () => {
      isMounted = false;
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [memoizedPdfDocument]);

  if (pdfVisible) {
    return (
      <div style={styles.pdfContainer}>
        <button
          className="cerrar-button"
          style={styles.closeButton}
          onClick={handleClosePDF}
        >
          <i className="bi bi-x-lg"></i>
        </button>
        <PDFViewer className="pdf-viewer" style={styles.pdfViewer}>
          {memoizedPdfDocument}
        </PDFViewer>
      </div>
    );
  }

  const handleEdit = () => {
    setEditMode(true);
    setShowSummary(false);
  };

  const closeModal = () => {
    setEditMode(false);
    setShowSummary(true);
    onClose();
  };

  if (editMode) {
    return (
      <EditarReporte
        reporte={reporte} // Aquí se pasa el objeto completo del reporte
        clientes={clientes}
        productos={reporte.productosSeleccionados}
        setReportes={setReportes}
        reportes={reportes}
        onClose={closeModal}
      />
    );
  }

  if (showSummary && reporte && reporte.productosSeleccionados) {
    return (
      <div className="resumen-cotizacion-container">
        <button className="cerrarBotonResumen" onClick={onClose}>
          <i className="bi bi-x-lg"></i>
        </button>
        <div className="resumen-cotizacion-actions">
          <div
            className="dropdown"
            onMouseEnter={() => setShowOptions(true)}
            onMouseLeave={() => setShowOptions(false)}
          >
            <button className="dropbtn">
              <i className="bi bi-printer iconResumen"></i> Imprimir / PDF
            </button>
            {showOptions && (
              <div className="dropdown-content">
                <button onClick={handlePrint}>
                  <i
                    className="bi bi-file-earmark-text"
                    style={{ marginRight: 10 }}
                  />
                  Imprimir
                </button>
                <a
                  href={pdfBlobUrl}
                  download={`${reporte.numeroObra || "sin_numero"}-${
                    reporte.nombreCliente || "sin_nombre"
                  }.pdf`}
                  style={{ textDecoration: "none" }}
                >
                  <button>
                    <i className="bi bi-download" style={{ marginRight: 10 }} />
                    Descargar PDF
                  </button>
                </a>
              </div>
            )}
          </div>
          <button>
            <i className="bi bi-envelope-fill iconResumen"></i> Correo
          </button>
          <button onClick={handleEdit}>
            <i className="bi bi-pencil-fill iconResumen"></i> Editar
          </button>
          <button>
            <i className="bi bi-receipt iconResumen"></i> Convertir en Factura
          </button>
          <div
            className="dropdown"
            onMouseEnter={() => setShowOptions(true)}
            onMouseLeave={() => setShowOptions(false)}
          >
            <button className="dropbtn">
              <i
                className="bi bi-three-dots-vertical iconResumen"
                style={{ marginRight: "0" }}
              ></i>
            </button>
            {showOptions && (
              <div className="dropdown-content">
                <button onClick={handleEliminar}>
                  <i
                    className="bi bi-trash"
                    style={{ marginRight: "10px" }}
                  ></i>
                  Eliminar
                </button>
                <button onClick={handleClonar}>
                  <i
                    className="bi bi-files"
                    style={{ marginRight: "10px" }}
                  ></i>
                  Clonar
                </button>
              </div>
            )}
          </div>
          {reporte.archivos && reporte.archivos.length > 0 && (
            <button onClick={() => setShowAdjuntosModal(true)}>
              <i className="bi bi-paperclip iconResumen"></i> Archivos Adjuntos
            </button>
          )}
        </div>

        <Modal
          isOpen={showAdjuntosModal}
          onRequestClose={() => setShowAdjuntosModal(false)}
          contentLabel="Archivos Adjuntos"
          style={{
            content: {
              top: "50%",
              left: "50%",
              right: "auto",
              bottom: "auto",
              marginRight: "-50%",
              transform: "translate(-50%, -50%)",
              width: "35%", // Tamaño del modal ajustado
              maxHeight: "80vh",
              overflowY: "auto",
              padding: "20px",
              borderRadius: "10px",
            },
            overlay: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          {/* Botón "X" para cerrar */}
          <button
            onClick={() => setShowAdjuntosModal(false)}
            className="modal-close"
            title="Cerrar"
          >
            <i className="bi bi-x-lg"></i>
          </button>

          {/* Título */}
          <h3
            style={{
              marginBottom: "20px",
              color: "#000",
              fontFamily: "Inter, sans-serif",
            }}
          >
            Archivos Adjuntos
          </h3>

          {/* Lista de Archivos Adjuntos */}
          <ul style={{ listStyle: "none", padding: 0 }}>
            {adjuntos.map((archivo, index) => {
              const { icon, color } = obtenerIconoArchivo(archivo.nombre);
              return (
                <li
                  key={index}
                  style={{
                    marginBottom: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "5px",
                  }}
                >
                  {/* Información del Archivo */}
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <i
                      className={`bi ${icon}`}
                      style={{
                        color: color,
                        fontSize: "1.5rem",
                        marginRight: "10px",
                      }}
                    ></i>
                    <a
                      href={archivo.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: "none",
                        color: "#000",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      {archivo.nombre}
                    </a>
                  </div>

                  {/* Acciones */}
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <a href={archivo.url} download={archivo.nombre}>
                      <button
                        onClick={(e) => {
                          e.preventDefault(); // Prevenir comportamiento predeterminado
                          const link = document.createElement("a");
                          link.href = archivo.url;
                          link.target = "_blank"; // Abrir en nueva pestaña
                          link.rel = "noopener noreferrer"; // Seguridad adicional
                          link.download = archivo.nombre; // Establecer nombre para descarga
                          document.body.appendChild(link);
                          link.click(); // Simular clic
                          document.body.removeChild(link); // Remover del DOM
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#007bff",
                          cursor: "pointer",
                          fontSize: "20px",
                        }}
                        title="Descargar archivo"
                      >
                        <i className="bi bi-download"></i>
                      </button>
                    </a>
                    <button
                      onClick={() => handleDeleteArchivo(archivo)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#dc3545",
                        cursor: "pointer",
                        marginRight: "10px",
                        fontSize: "20px",
                      }}
                      title="Eliminar archivo"
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </Modal>
        <div
          style={{
            height: "600px",
            width: "100%",
            marginTop: "-20px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {loading ? (
            <div className="loader2"></div>
          ) : (
            pdfBlobUrl && (
              <iframe
                src={`${pdfBlobUrl}#toolbar=0`}
                style={{ width: "100%", height: "100%" }}
                frameBorder="0"
                title="Vista previa del PDF"
              />
            )
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default ResumenReporte;
